/**
 * Catalogue des permissions métier (source de vérité, versionné ici et seedé en base).
 * Référence : docs/security/SECURITY_ARCHITECTURE.md §3.2 + décisions D-017, D-020, D-022 (2026-09-04).
 * Convention : `domaine.action`. ★ = sensible (audit systématique). ⇑ = à haut privilège.
 */
export const PERMISSION_DOMAINS = [
    'tenant',
    'settings',
    'user',
    'resource',
    'reservation',
    'customer',
    'compliance',
    'document',
    'task',
    'proforma',
    'invoice',
    'payment',
    'cash',
    'expense',
    'housekeeping',
    'maintenance',
    'notification',
    'report',
    'audit',
    'platform',
];
const p = (code, domain, description, flags = {}) => ({ code, domain, description, ...flags });
export const PERMISSIONS = [
    // tenant & properties
    p('tenant.read', 'tenant', 'Consulter la fiche du tenant (raison sociale, RCCM, IFU)'),
    p('tenant.update', 'tenant', 'Modifier la fiche tenant (mentions légales des factures)', {
        sensitive: true,
    }),
    p('property.read', 'tenant', 'Consulter les établissements'),
    p('property.create', 'tenant', 'Créer un établissement', { sensitive: true }),
    p('property.update', 'tenant', 'Modifier un établissement'),
    // settings
    p('settings.read', 'settings', 'Consulter les paramètres'),
    p('settings.update', 'settings', 'Modifier les paramètres généraux (numérotation, devise, politiques)', { sensitive: true }),
    p('settings.update_security', 'settings', 'Modifier les paramètres de sécurité (timeouts, MFA obligatoire, seuils)', { sensitive: true, highPrivilege: true }),
    p('branding.update', 'settings', 'Modifier logo, couleurs, pied de page des documents'),
    p('tax.read', 'settings', 'Consulter les règles fiscales'),
    p('tax.manage', 'settings', 'Créer/modifier les règles de taxes, retenues, exonérations', {
        sensitive: true,
        highPrivilege: true,
    }),
    // users & roles
    p('user.read', 'user', 'Lister les utilisateurs du tenant'),
    p('user.create', 'user', 'Inviter un utilisateur', { sensitive: true }),
    p('user.update', 'user', "Modifier le profil ou l'e-mail d'un utilisateur", { sensitive: true }),
    p('user.deactivate', 'user', 'Désactiver / réactiver un compte', { sensitive: true }),
    p('user.reset_password', 'user', 'Déclencher une réinitialisation pour un autre utilisateur', {
        sensitive: true,
    }),
    p('role.read', 'user', 'Consulter rôles et permissions'),
    p('role.manage', 'user', 'Créer/modifier/supprimer des rôles personnalisés', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('role.assign', 'user', 'Attribuer/retirer des rôles (avec scope établissement)', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('session.revoke', 'user', "Révoquer les sessions d'un autre utilisateur", { sensitive: true }),
    p('mfa.enforce', 'user', 'Rendre la MFA obligatoire pour un rôle', { sensitive: true }),
    // resources
    p('resource.read', 'resource', 'Consulter hébergements et salles'),
    p('resource.create', 'resource', 'Créer une ressource'),
    p('resource.update', 'resource', 'Modifier une ressource (photos, caractéristiques)'),
    p('resource.archive', 'resource', 'Archiver une ressource', { sensitive: true }),
    p('rateplan.read', 'resource', 'Consulter les tarifs'),
    p('rateplan.manage', 'resource', 'Créer/modifier les plans tarifaires', { sensitive: true }),
    p('availability.read', 'resource', 'Consulter le calendrier de disponibilité'),
    p('availability.block', 'resource', 'Créer un blocage / bloc de maintenance'),
    // reservations
    p('reservation.read', 'reservation', 'Consulter les réservations'),
    p('reservation.create', 'reservation', 'Créer une réservation (brouillon / en attente)'),
    p('reservation.update', 'reservation', 'Modifier dates, occupants, extras (avant check-out)'),
    p('reservation.hold', 'reservation', 'Poser une option (blocage temporaire)'),
    p('reservation.confirm', 'reservation', 'Confirmer une réservation (avance enregistrée requise)'),
    p('reservation.confirm.without_deposit', 'reservation', 'Confirmer sans avance, motif obligatoire (D-020)', { sensitive: true }),
    p('reservation.cancel', 'reservation', 'Annuler (motif obligatoire, frais éventuels)', {
        sensitive: true,
    }),
    p('reservation.checkin', 'reservation', 'Effectuer le check-in'),
    p('reservation.checkout', 'reservation', 'Effectuer le check-out'),
    p('reservation.checkout.with_balance', 'reservation', 'Effectuer un check-out avec solde impayé, motif obligatoire (R-010)', { sensitive: true }),
    p('reservation.mark_noshow', 'reservation', 'Déclarer un no-show', { sensitive: true }),
    p('reservation.override_price', 'reservation', 'Réviser librement le tarif et la remise (latitude manager, D-022)', { sensitive: true }),
    // customers
    p('customer.read', 'customer', 'Consulter les clients (personnes, entreprises)'),
    p('customer.create', 'customer', 'Créer un client'),
    p('customer.update', 'customer', 'Modifier un client'),
    p('customer.merge', 'customer', 'Fusionner des doublons', { sensitive: true }),
    p('customer.export', 'customer', 'Exporter des données clients', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('customer.anonymize', 'customer', "Pseudonymiser un client (droit à l'effacement)", {
        sensitive: true,
        highPrivilege: true,
    }),
    // compliance
    p('compliance.read', 'compliance', "Consulter l'état de conformité d'un séjour"),
    p('compliance.validate', 'compliance', "Valider ou rejeter une pièce justificative d'un séjour (D-013)", { sensitive: true }),
    p('compliance.manage_requirements', 'compliance', 'Configurer les exigences documentaires par type de ressource', { sensitive: true }),
    // documents
    p('document.read', 'document', 'Consulter les métadonnées des documents'),
    p('document.upload', 'document', 'Téléverser un document'),
    p('document.view_identity', 'document', "Ouvrir une pièce d'identité (accès journalisé nominativement)", { sensitive: true }),
    p('document.download', 'document', 'Télécharger les documents non sensibles (PDF facture, reçu)'),
    p('document.delete', 'document', 'Supprimer un document avant validation (motif)', {
        sensitive: true,
    }),
    // tâches (centre de tâches : documents manquants, paiement attendu…)
    p('task.read', 'task', 'Consulter le centre de tâches'),
    p('task.update', 'task', 'Traiter, clôturer ou écarter une tâche'),
    // proformas
    p('proforma.read', 'proforma', 'Consulter les proformas'),
    p('proforma.create', 'proforma', 'Créer / modifier un brouillon de proforma'),
    p('proforma.issue', 'proforma', 'Émettre une proforma (numérotation PRO/AAAA/NNNNN)'),
    p('proforma.cancel', 'proforma', 'Annuler une proforma'),
    // invoices
    p('invoice.read', 'invoice', 'Consulter les factures'),
    p('invoice.create', 'invoice', 'Créer / modifier un brouillon de facture'),
    p('invoice.issue', 'invoice', 'Émettre une facture définitive (contrôle de conformité R-001)', {
        sensitive: true,
    }),
    p('invoice.issue.override_compliance', 'invoice', 'Émettre malgré des documents manquants — dérogation R-001 avec motif obligatoire (D-017, Owner/Manager)', { sensitive: true, highPrivilege: true }),
    p('invoice.apply_discount', 'invoice', 'Appliquer une remise'),
    p('invoice.download_pdf', 'invoice', 'Télécharger le PDF'),
    p('creditnote.create', 'invoice', "Créer un avoir (seule voie d'annulation d'une facture émise)", { sensitive: true, highPrivilege: true }),
    // payments
    p('payment.read', 'payment', 'Consulter les paiements'),
    p('payment.record', 'payment', 'Enregistrer un paiement (espèces, Orange Money, virement…)'),
    p('payment.allocate', 'payment', 'Affecter un paiement à une facture / réservation'),
    p('payment.void', 'payment', 'Annuler un paiement (écriture compensatoire, motif obligatoire)', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('payment.refund', 'payment', 'Enregistrer un remboursement', {
        sensitive: true,
        highPrivilege: true,
    }),
    // cash
    p('cash.read', 'cash', 'Consulter les sessions de caisse'),
    p('cash.open_session', 'cash', 'Ouvrir une session de caisse'),
    p('cash.record_movement', 'cash', 'Saisir un mouvement de caisse hors paiement'),
    p('cash.close_session', 'cash', 'Clôturer une session (comptage, écart)', { sensitive: true }),
    p('cash.reconcile', 'cash', 'Valider le rapprochement / justifier un écart', { sensitive: true }),
    // expense (sprint 2.9, D-052)
    p('expense.read', 'expense', 'Consulter les dépenses'),
    p('expense.record', 'expense', 'Enregistrer une dépense (espèces, mobile money, chèque, banque)'),
    p('expense.cancel', 'expense', 'Annuler une dépense (motif obligatoire)', { sensitive: true }),
    p('expense.export', 'expense', 'Exporter les dépenses', { sensitive: true }),
    // housekeeping & maintenance
    p('housekeeping.read', 'housekeeping', 'Consulter le statut ménage'),
    p('housekeeping.update_status', 'housekeeping', 'Changer le statut ménage'),
    p('maintenance.read', 'maintenance', 'Consulter les incidents'),
    p('maintenance.create', 'maintenance', 'Déclarer un incident'),
    p('maintenance.resolve', 'maintenance', 'Clôturer un incident / lever un blocage'),
    // notifications
    p('notification.read', 'notification', "Consulter l'historique des envois"),
    p('notification.send', 'notification', 'Envoyer manuellement un message au client'),
    p('notification.manage_templates', 'notification', 'Modifier les modèles de messages', {
        sensitive: true,
    }),
    // reports
    p('report.operational', 'report', "Rapports d'occupation, arrivées/départs"),
    p('report.financial', 'report', 'Rapports CA, encaissements, impayés', { sensitive: true }),
    p('report.export', 'report', 'Exporter des rapports', { sensitive: true }),
    // audit
    p('audit.read', 'audit', "Consulter le journal d'audit du tenant", { sensitive: true }),
    p('audit.export', 'audit', "Exporter le journal d'audit", {
        sensitive: true,
        highPrivilege: true,
    }),
    // platform
    p('platform.manage_tenants', 'platform', 'Créer/suspendre des tenants, gérer les abonnements', {
        sensitive: true,
        highPrivilege: true,
        platformOnly: true,
    }),
    p('platform.support_access', 'platform', 'Accéder en lecture à un tenant avec accord du Owner (journalisé)', { sensitive: true, highPrivilege: true, platformOnly: true }),
    p('platform.read_health', 'platform', 'Consulter la santé de la plateforme', {
        platformOnly: true,
    }),
    p('platform.manage_permissions', 'platform', 'Faire évoluer le catalogue de permissions', {
        sensitive: true,
        highPrivilege: true,
        platformOnly: true,
    }),
];
export const PERMISSION_CODES = PERMISSIONS.map((x) => x.code);
export const isPermissionCode = (v) => PERMISSION_CODES.includes(v);
/** Rôles système (docs/security/SECURITY_ARCHITECTURE.md §3.3, D-011). */
export const SYSTEM_ROLE_CODES = [
    'owner',
    'manager',
    'front_desk',
    'accountant',
    'housekeeping',
    'read_only',
    'platform_support',
];
const all = (prefixes) => PERMISSION_CODES.filter((c) => prefixes.some((pre) => c.startsWith(pre)));
const READ_ALL = PERMISSION_CODES.filter((c) => /\.(read|read_health)$/.test(c) && !c.startsWith('platform.'));
/** Matrice rôle × permissions par défaut (seed). Modifiable uniquement via rôles personnalisés. */
export const SYSTEM_ROLE_PERMISSIONS = {
    owner: PERMISSION_CODES.filter((c) => !c.startsWith('platform.')),
    manager: PERMISSION_CODES.filter((c) => !c.startsWith('platform.') &&
        ![
            'settings.update_security',
            'tax.manage',
            'customer.anonymize',
            'audit.export',
            'platform.manage_permissions',
        ].includes(c)),
    front_desk: [
        ...all(['tenant.read', 'property.read', 'settings.read', 'tax.read']),
        ...all(['resource.read', 'rateplan.read', 'availability.']),
        'reservation.read',
        'reservation.create',
        'reservation.update',
        'reservation.hold',
        'reservation.confirm',
        'reservation.checkin',
        'reservation.checkout',
        'reservation.cancel',
        ...all(['customer.read', 'customer.create', 'customer.update']),
        ...all([
            'compliance.read',
            'compliance.validate',
            'document.read',
            'document.upload',
            'document.view_identity',
            'document.download',
        ]),
        'task.read',
        'task.update',
        ...all(['proforma.']),
        'invoice.read',
        'invoice.create',
        'invoice.apply_discount',
        'invoice.download_pdf',
        'payment.read',
        'payment.record',
        'payment.allocate',
        'cash.read',
        'cash.open_session',
        'cash.record_movement',
        'expense.read',
        'expense.record',
        ...all(['housekeeping.', 'maintenance.read', 'maintenance.create']),
        'notification.read',
        'notification.send',
        'report.operational',
    ],
    accountant: [
        ...all([
            'tenant.read',
            'property.read',
            'settings.read',
            'tax.read',
            'resource.read',
            'rateplan.read',
            'availability.read',
        ]),
        'reservation.read',
        'customer.read',
        'customer.export',
        'expense.read',
        'expense.record',
        'expense.export',
        'compliance.read',
        'document.read',
        'document.download',
        'task.read',
        ...all(['proforma.', 'invoice.']).filter((c) => c !== 'invoice.issue.override_compliance'),
        'creditnote.create',
        ...all(['payment.', 'cash.']),
        'notification.read',
        // La comptabilité émet les factures : elle doit pouvoir les transmettre au
        // client (`POST /invoices/:id/send`, sprint 2.6).
        'notification.send',
        'report.operational',
        'report.financial',
        'report.export',
        'audit.read',
    ],
    housekeeping: [
        'property.read',
        'resource.read',
        'availability.read',
        'housekeeping.read',
        'housekeeping.update_status',
        'maintenance.read',
        'maintenance.create',
        'task.read',
        'task.update',
    ],
    read_only: READ_ALL.filter((c) => !['document.view_identity', 'audit.read'].includes(c)),
    platform_support: ['platform.read_health', 'platform.support_access'],
};
//# sourceMappingURL=permissions.js.map